import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface HomeComponentConfig {
  activo: boolean;
  orden: number;
}

export interface AccesosDirectosConfig extends HomeComponentConfig {
  componentes: {
    misFarmacos: boolean;
    misCitas: boolean;
    misCuestionarios: boolean;
    misAnalisis: boolean;
    chatbot: boolean;
    incidenciasMedicas: boolean;
  };
}

export interface HomeConfig {
  bloqueSolicitarCita: HomeComponentConfig;
  bloqueAccesosDirectos: AccesosDirectosConfig;
  bloqueNotificaciones: HomeComponentConfig;
  bloqueInformacionConsejos: HomeComponentConfig;
  bloqueChatbot: HomeComponentConfig;
}

@Injectable({
  providedIn: 'root'
})
export class HomeConfigService {
  private configSubject = new BehaviorSubject<HomeConfig>(this.getDefaultConfig());
  
  public readonly config$ = this.configSubject.asObservable();

  constructor() {
    this.initConfig();
  }

  private getDefaultConfig(): HomeConfig {
    return {
      bloqueSolicitarCita: {
        activo: true,
        orden: 1
      },
      bloqueAccesosDirectos: {
        activo: true,
        componentes: {
          misFarmacos: true,
          misCitas: true,
          misCuestionarios: true,
          misAnalisis: true,
          chatbot: true,
          incidenciasMedicas: true
        },
        orden: 2
      },
      bloqueNotificaciones: {
        activo: true,
        orden: 3
      },
      bloqueInformacionConsejos: {
        activo: true,
        orden: 4
      },
      bloqueChatbot: {
        activo: true,
        orden: 5
      }
    };
  }

  private initConfig(): void {
    const savedConfig = localStorage.getItem('homeConfig');
    
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig) as HomeConfig;
        this.configSubject.next(parsedConfig);
      } catch (error) {
        console.error('Error al parsear la configuración guardada:', error);
        this.resetConfig();
      }
    }
  }

  public getConfig(): HomeConfig {
    return this.configSubject.getValue();
  }

  public updateConfig(config: HomeConfig): void {
    localStorage.setItem('homeConfig', JSON.stringify(config));
    this.configSubject.next(config);
  }

  public resetConfig(): void {
    const defaultConfig = this.getDefaultConfig();
    localStorage.setItem('homeConfig', JSON.stringify(defaultConfig));
    this.configSubject.next(defaultConfig);
  }

  public updateComponentActivo(componentName: keyof HomeConfig, activo: boolean): void {
    const currentConfig = this.getConfig();
    const updatedConfig = {
      ...currentConfig,
      [componentName]: {
        ...currentConfig[componentName],
        activo
      }
    };
    this.updateConfig(updatedConfig);
  }

  public updateComponentOrden(componentName: keyof HomeConfig, orden: number): void {
    const currentConfig = this.getConfig();
    const updatedConfig = {
      ...currentConfig,
      [componentName]: {
        ...currentConfig[componentName],
        orden
      }
    };
    this.updateConfig(updatedConfig);
  }

  public updateAccesoDirectoActivo(acceso: keyof AccesosDirectosConfig['componentes'], activo: boolean): void {
    const currentConfig = this.getConfig();
    const updatedConfig = {
      ...currentConfig,
      bloqueAccesosDirectos: {
        ...currentConfig.bloqueAccesosDirectos,
        componentes: {
          ...currentConfig.bloqueAccesosDirectos.componentes,
          [acceso]: activo
        }
      }
    };
    this.updateConfig(updatedConfig);
  }

  public getOrderedComponents(): Array<{key: keyof HomeConfig, config: HomeComponentConfig}> {
    const config = this.getConfig();
    
    return Object.entries(config)
      .map(([key, value]) => ({
        key: key as keyof HomeConfig, 
        config: value as HomeComponentConfig
      }))
      .sort((a, b) => a.config.orden - b.config.orden);
  }
  
  public getActiveComponents(): Array<{key: keyof HomeConfig, config: HomeComponentConfig}> {
    const config = this.getConfig();
    
    return Object.entries(config)
      .map(([key, value]) => ({
        key: key as keyof HomeConfig, 
        config: value as HomeComponentConfig
      }))
      .filter(item => item.config.activo)
      .sort((a, b) => a.config.orden - b.config.orden);
  }

  public getDefaulHomeConfig(): any {
    return {
      "id": 1,
      "uuid": "cd5b8566-7afc-4748-9e4c-3c204203f292",
      "bloqueSolicitarCita": {
        "activo": true,
        "orden": 1
      },
      "bloqueAccesosDirectos": {
        "activo": true,
        "orden": 2,
        "componentes": {
          "misFarmacos": true,
          "misCitas": true,
          "misCuestionarios": true,
          "misAnalisis": true,
          "incidenciasMedicas": true,
          "chatbot": true
        }
      },
      "bloqueNotificaciones": {
        "activo": true,
        "orden": 3
      },
      "bloqueInformacionConsejos": {
        "activo": true,
        "orden": 4
      },
      "bloqueChatbot": {
        "activo": true
      }
    }
  }
} 